import { useState } from "react";
import { FlipHorizontal2, Trash2 } from "lucide-react";
import { ROTULO_DO_TIPO_DE_MARCACAO, regioesDaVista, type MarcacaoFacial } from "@bookdental/schemas";
import { Button, Input, Label } from "@bookdental/ui";

const campo = "h-9 w-full rounded-md border border-zinc-200 bg-white px-2 text-sm dark:border-border dark:bg-card";
const paraTexto = (q: number | null | undefined) => (q == null ? "" : String(q).replace(".", ","));

/**
 * Dados da marcação selecionada, embaixo do desenho: região (sugerida no clique, trocável),
 * quantidade na unidade do procedimento e observação. Espelhar cria uma cópia independente no lado
 * oposto; o tamanho do desenho nunca é dose.
 */
export function PainelDaMarcacao({
  m,
  numero,
  unidade,
  onMudar,
  onEspelhar,
  onRemover,
}: {
  m: MarcacaoFacial;
  numero: number;
  unidade: string | null;
  onMudar: (m: MarcacaoFacial) => void;
  onEspelhar: () => void;
  onRemover: () => void;
}) {
  const [quantidade, setQuantidade] = useState(paraTexto(m.quantidade));
  const regioes = regioesDaVista(m.vista);

  const mudarQuantidade = (texto: string) => {
    setQuantidade(texto);
    const n = Number(texto.replace(",", "."));
    onMudar({ ...m, quantidade: texto.trim() && Number.isFinite(n) && n >= 0 ? Math.round(n * 100) / 100 : null });
  };

  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-zinc-50 p-3 dark:border-border dark:bg-muted/40">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-zinc-700 dark:text-foreground">
          {ROTULO_DO_TIPO_DE_MARCACAO[m.tipo]} {numero}
        </p>
        <div className="flex gap-1">
          <Button type="button" variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={onEspelhar} title="Cria uma cópia no lado oposto">
            <FlipHorizontal2 className="h-3.5 w-3.5" /> Espelhar
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 px-2 text-red-600 hover:text-red-700" onClick={onRemover} aria-label="Remover marcação">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      <div className="space-y-1">
        <Label htmlFor="marcacao-regiao" className="text-xs">Região</Label>
        <select id="marcacao-regiao" value={m.regiao ?? ""} onChange={(e) => onMudar({ ...m, regiao: e.target.value || null })} className={campo}>
          <option value="">Sem região</option>
          {regioes.map((r) => (
            <option key={r.codigo} value={r.codigo}>
              {r.rotulo}
            </option>
          ))}
        </select>
        {!m.regiao && <p className="text-[11px] text-zinc-500">Fora das regiões conhecidas: escolha na lista, se quiser.</p>}
      </div>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] gap-2">
        <div className="space-y-1">
          <Label htmlFor="marcacao-quantidade" className="text-xs">Quantidade{unidade ? ` (${unidade})` : ""}</Label>
          <Input id="marcacao-quantidade" inputMode="decimal" value={quantidade} placeholder="Opcional" onChange={(e) => mudarQuantidade(e.target.value)} className="h-9" />
        </div>
        <div className="space-y-1">
          <Label htmlFor="marcacao-obs" className="text-xs">Observação</Label>
          <Input
            id="marcacao-obs"
            value={m.observacao ?? ""}
            maxLength={200}
            placeholder="Opcional"
            onChange={(e) => onMudar({ ...m, observacao: e.target.value || null })}
            className="h-9"
          />
        </div>
      </div>
    </div>
  );
}
